import type { AxiosError, GenericAbortSignal } from 'axios';
import axios from 'axios';

import i18next from '@/i18n';
import { ROUTES } from '@/shared/constants';
import { getAppNavigate } from '@/shared/services/helpers/navigate-helper';
import {
  getClientToken,
  removeClientToken,
  setClientToken
} from '@/shared/services/user-hash';

export type CustomAxiosError = AxiosError<{
  message?: string;
  statusCode: number;
}>;

export class ApiConnector {
  async call<Request, Response>({
    path,
    method = 'get',
    body,
    params,
    abortSignal,
    tokenOverride
  }: {
    path: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    body?: Request;
    params?: Request;
    abortSignal?: GenericAbortSignal | undefined;
    tokenOverride?: string;
  }): Promise<Response> {
    const clonedParams = structuredClone(params);
    for (const param in clonedParams) {
      if (clonedParams[param] === undefined || !String(clonedParams[param]))
        delete clonedParams[param];
    }

    try {
      const client = getClientToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      const authToken = tokenOverride ?? client?.accessToken;
      if (authToken) headers.Authorization = `Bearer ${authToken}`;

      const { data } = await axios(`${process.env.PUBLIC_AUTH_URL}/` + path, {
        method,
        data: body,
        params: clonedParams,
        headers,
        signal: abortSignal
      });
      return data;
    } catch (e: unknown) {
      const error = e as CustomAxiosError;

      if ([401, 403].includes(error?.response?.status || -1)) {
        const navigate = getAppNavigate();
        removeClientToken();
        const newPath = `/${i18next.language}/${ROUTES.create}`;
        const savedUrl =
          window.location.pathname !== newPath &&
          `${window.location.pathname}${window.location.search}`;
        navigate(newPath, { state: { savedUrl } });
      }

      const statusCode =
        error?.response?.data?.statusCode || error?.response?.status;
      let message = i18next.t('api_generic_error');
      if (statusCode && statusCode !== 404 && statusCode < 500) {
        message = error?.response?.data?.message || message;
      }
      if (Array.isArray(message)) message = message.join('\n');

      throw {
        message,
        statusCode,
        isCancelled: axios.isCancel(e)
      };
    }
  }

  downloadBankFile(
    uuid: string
  ): Promise<{ base64: string; name: string; blob?: () => Promise<Blob> }> {
    return this.call({ path: `files/${uuid}/downloadmanually` });
  }

  public async clientLogin(
    pinfl: string,
    phoneNumber: string,
    dealerGroupId: string
  ): Promise<{
    accessToken: string;
    refreshToken: string;
    client: {
      uuid: string;
      first_name: string;
      last_name: string;
      middle_name: string;
      phone_number: string;
      personal_id: string;
    };
  }> {
    try {
      const res = await axios({
        url: `${process.env.PUBLIC_AUTH_URL}/online-clients/login`,
        method: 'post',
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          pinfl,
          phone_number: phoneNumber,
          dealerGroupId
        }
      });
      const { accessToken, refreshToken } = res.data;

      if (accessToken) {
        setClientToken({ accessToken, refreshToken, personalId: pinfl });
      }

      return res.data;
    } catch (e: unknown) {
      const error = e as CustomAxiosError;
      throw error?.response?.data;
    }
  }

  public async refreshToken(): Promise<void> {
    try {
      const { refreshToken: token, personalId } = getClientToken();
      const res = await axios({
        url: `${process.env.PUBLIC_AUTH_URL}/online-clients/refresh`,
        method: 'post',
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const {
        data: { accessToken, refreshToken }
      } = res;
      if (accessToken && refreshToken) {
        setClientToken({ accessToken, refreshToken, personalId });
      } else {
        removeClientToken();
      }
    } catch {
      throw new Error('Refresh Token Expired! Logging out');
    }
  }
}

export const apiConnector = new ApiConnector();
